import { AutofocusDirective } from '../../../../../../shared/directive/autofocus.directive';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDatepickerMaskDirective } from '@anihis/shared/src/shared/directive/mat-datepicker-mask.directive';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormBaseComponent } from '@anihis/shared/src/shared/base-components/form-base.component';
import isEqual from 'lodash/isEqual';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { TranslocoModule } from '@jsverse/transloco';
import {
  getMaxBirthDate,
  getMinAnimalBirthDate,
} from '@anihis/shared/src/validators/birth-date.validators';

@Component({
  selector: 'anihis-cardio-pulmonary-dirofilariasis',
  standalone: true,
  imports: [
    AutofocusDirective,
    CommonModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatDatepickerMaskDirective,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslocoModule,
  ],
  templateUrl: './cardio-pulmonary-dirofilariasis.component.html',
  styleUrls: ['./cardio-pulmonary-dirofilariasis.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardioPulmonaryDirofilariasisComponent
  extends FormBaseComponent
  implements OnInit
{
  minDate = getMinAnimalBirthDate();
  today = getMaxBirthDate();

  @Output() formChange = new EventEmitter<any>();

  rows = [0, 1, 2, 3, 4];
  days = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];
  tests = [
    { name: "Modified Knot's Test", control: 'modified-knots' },
    { name: 'Ag Test', control: 'ag-test' },
    { name: 'Manufacturer / Serial Number', control: 'manufacturer' },
  ];
  dates = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  headers = Array.from({ length: 9 });
  ivermectinDates = Array.from({ length: 12 }, (_, i) => i);
  pickerArray = Array.from({ length: 39 }, (_, i) => `picker${i + 1}`);

  testsForm: FormGroup;
  diseaseStageForm: FormGroup;
  clinicalProblems: FormGroup;
  ivermectin: FormGroup;
  dayForm: FormGroup;
  override form: FormGroup;

  constructor() {
    super();
    this.testsForm = this.fb.group(
      this.generateFormGroupConfig(this.tests, this.dates),
    );

    this.diseaseStageForm = this.fb.group({
      mild: '',
      moderate: '',
      serious: '',
      cavae: '',
    });

    this.clinicalProblems = this.fb.group({
      note: '',
    });

    this.ivermectin = this.fb.group(
      this.generateFormGroupConfigForIvermectin(),
    );

    this.dayForm = this.fb.group(this.initializeFormControls());

    this.form = this.fb.group({
      testsForm: this.testsForm,
      diseaseStageForm: this.diseaseStageForm,
      clinicalProblems: this.clinicalProblems,
      ivermectin: this.ivermectin,
      dayForm: this.dayForm,
      veterinarianName: new FormControl(''),
    });

    this.initializeFormControls();
  }

  override ngOnInit(): void {
    this.subscribeToFormValueChanges(this.form);
  }

  generateFormGroupConfig(tests: any[], dates: number[]): any {
    const config: any = {};
    tests.forEach((test) => {
      dates.forEach((date) => {
        config[`${test.control}-${date}`] = new FormControl(null);
      });
    });
    return config;
  }

  generateFormGroupConfigForIvermectin(): any {
    const config: any = {};
    for (let i = 1; i <= 12; i++) {
      config[`application-date-${i}`] = new FormControl(null);
    }
    return config;
  }

  initializeFormControls() {
    const controlConfig: any = {};
    this.rows.forEach((row) => {
      this.days.forEach((day) => {
        const controlName1 = this.getControlName(day, row, 1);
        const controlName2 = this.getControlName(day, row, 2);
        controlConfig[controlName1] = new FormControl('');
        controlConfig[controlName2] = new FormControl('');
      });
    });
    return controlConfig;
  }

  private subscribeToFormValueChanges(form: FormGroup) {
    form.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(isEqual))
      .subscribe((value) => this.formChange.emit(value));
  }

  getControlName(day: string, row: number, inputNumber: number): string {
    return `${day.toLowerCase()}_row${row + 1}_input${inputNumber}`;
  }
}
